import React, { useState } from 'react';
import { useParams, useLocation } from 'react-router-dom';

import { useSnackbarContext } from '../../base/snackbar';
import { ProjectData } from '../../dashboard/compliance';

import { dashboardRouteUtil } from '~/client/routes';
import * as GQL from '~/graphql/generated/client';

interface ModelsDashboardProps {
  loading: boolean;
  datasetLoading: boolean;
  functionalModelsLoading: boolean;
  agentsLoading: boolean;
  modelsList: ProjectListResponse;
  datasetList: ProjectListResponse;
  functionalModelsList: ProjectListResponse;
  agentsList: ProjectListResponse;
  error: any;
  refetchModels?: () => Promise<any>;
  refetchFunctionalModels?: () => Promise<any>;
  refetchDatasets?: () => Promise<any>;
  refetchAgents?: () => Promise<any>;
  functionalModelsPage: number;
  setFunctionalModelsPage: React.Dispatch<React.SetStateAction<number>>;
  functionalModelsPageSize: number;
  setFunctionalModelsPageSize: React.Dispatch<React.SetStateAction<number>>;
  setAiSystemPage: React.Dispatch<React.SetStateAction<number>>;
  aiSystemPage: number;
  aiSystemPageSize: number;
  setAiSystemPageSize: React.Dispatch<React.SetStateAction<number>>;
  datasetPage: number;
  datasetPageSize: number;
  setDatasetPageSize: React.Dispatch<React.SetStateAction<number>>;
  setDatasetPage: React.Dispatch<React.SetStateAction<number>>;
  agentsPage: number;
  setAgentsPage: React.Dispatch<React.SetStateAction<number>>;
  agentsPageSize: number;
  setAgentsPageSize: React.Dispatch<React.SetStateAction<number>>;
  functionalModelsTotalCount: number;
  aiSystemListLoading: boolean;
  aiSystemList: GQL.AiSystemListQuery | undefined;
  aiSystemListError: any;
  refetchAiSystemList: () => Promise<any>;
  aiSystemSearch: string;
  setAiSystemSearch: React.Dispatch<React.SetStateAction<string>>;
  sortDirection: GQL.SortDirection | null;
  setSortDirection: React.Dispatch<
    React.SetStateAction<GQL.SortDirection | null>
  >;
  riskFilter: GQL.RiskFilter;
  setRiskFilter: React.Dispatch<React.SetStateAction<GQL.RiskFilter>>;
  vendorModelsList: ProjectListResponse;
  vendorModelsLoading: boolean;
  vendorModelsError: any;
  refetchVendorModels?: () => Promise<any>;
  refetchVendorAgents?: () => Promise<any>;
  vendorModelsPage: number;
  setVendorModelsPage: React.Dispatch<React.SetStateAction<number>>;
  vendorModelsPageSize: number;
  setVendorModelsPageSize: React.Dispatch<React.SetStateAction<number>>;
  vendorModelsSearch: string;
  setVendorModelsSearch: React.Dispatch<React.SetStateAction<string>>;
  organizationUnitId?: string | null;
  setOrganizationUnitId?: React.Dispatch<React.SetStateAction<string | null>>;
  vendorAgentsList: ProjectListResponse;
  vendorAgentsLoading: boolean;
  vendorAgentsError: any;
  vendorAgentsPage: number;
  setVendorAgentsPage: React.Dispatch<React.SetStateAction<number>>;
  vendorAgentsPageSize: number;
  setVendorAgentsPageSize: React.Dispatch<React.SetStateAction<number>>;
  vendorAgentsSearch: string;
  setVendorAgentsSearch: React.Dispatch<React.SetStateAction<string>>;
}

interface ProjectListResponse {
  data: ProjectData[];
  totalCount: number;
}

const ModelsDashboardContext = React.createContext<ModelsDashboardProps>({
  loading: true,
  datasetLoading: true,
  functionalModelsLoading: true,
  agentsLoading: true,
  modelsList: { data: [], totalCount: 0 },
  datasetList: { data: [], totalCount: 0 },
  functionalModelsList: { data: [], totalCount: 0 },
  agentsList: { data: [], totalCount: 0 },
  error: null,
  functionalModelsPage: 0,
  setFunctionalModelsPage: () => {},
  functionalModelsPageSize: 10,
  setFunctionalModelsPageSize: () => {},
  setAiSystemPage: () => {},
  aiSystemPage: 0,
  aiSystemPageSize: 10,
  setAiSystemPageSize: () => {},
  datasetPage: 0,
  datasetPageSize: 10,
  setDatasetPageSize: () => {},
  setDatasetPage: () => {},
  agentsPage: 0,
  setAgentsPage: () => {},
  agentsPageSize: 10,
  setAgentsPageSize: () => {},
  functionalModelsTotalCount: 0,
  aiSystemListLoading: true,
  aiSystemList: undefined,
  aiSystemListError: null,
  refetchAiSystemList: () => Promise.resolve(),
  aiSystemSearch: '',
  setAiSystemSearch: () => {},
  sortDirection: null,
  setSortDirection: () => {},
  riskFilter: GQL.RiskFilter.All,
  setRiskFilter: () => {},
  vendorModelsList: { data: [], totalCount: 0 },
  vendorModelsLoading: true,
  vendorModelsError: null,
  vendorModelsPage: 0,
  setVendorModelsPage: () => {},
  vendorModelsPageSize: 10,
  setVendorModelsPageSize: () => {},
  vendorModelsSearch: '',
  setVendorModelsSearch: () => {},
  organizationUnitId: null,
  setOrganizationUnitId: () => {},
  vendorAgentsList: { data: [], totalCount: 0 },
  vendorAgentsLoading: true,
  vendorAgentsError: null,
  vendorAgentsPage: 0,
  setVendorAgentsPage: () => {},
  vendorAgentsPageSize: 10,
  setVendorAgentsPageSize: () => {},
  vendorAgentsSearch: '',
  setVendorAgentsSearch: () => {},
});

export const useAIResourcesContext = () =>
  React.useContext(ModelsDashboardContext);

export const AIResourceProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const Snackbar = useSnackbarContext();
  // get data from backend
  const { projectId, functionalModelId } = useParams();
  const location = useLocation();
  // project states
  const [modelsList, setModelsList] = React.useState<ProjectListResponse>({
    data: [],
    totalCount: 0,
  });
  const [datasetList, setDatasetList] = React.useState<ProjectListResponse>({
    data: [],
    totalCount: 0,
  });
  const [agentsList, setAgentsList] = React.useState<ProjectListResponse>({
    data: [],
    totalCount: 0,
  });
  const [functionalModelsList, setFunctionalModelsList] =
    React.useState<ProjectListResponse>({
      data: [],
      totalCount: 0,
    });
  const [functionalModelsPage, setFunctionalModelsPage] = useState(0);
  const [functionalModelsPageSize, setFunctionalModelsPageSize] = useState(10);
  const [aiSystemPage, setAiSystemPage] = useState(0);
  const [aiSystemPageSize, setAiSystemPageSize] = useState(10);
  const [datasetPage, setDatasetPage] = useState(0);
  const [datasetPageSize, setDatasetPageSize] = useState(10);
  const [agentsPage, setAgentsPage] = useState(0);
  const [agentsPageSize, setAgentsPageSize] = useState(10);
  const [riskFilter, setRiskFilter] = useState<GQL.RiskFilter>(
    GQL.RiskFilter.All
  );
  const [sortDirection, setSortDirection] = useState<GQL.SortDirection | null>(
    null
  );
  const [vendorModelsPage, setVendorModelsPage] = useState(0);
  const [vendorModelsPageSize, setVendorModelsPageSize] = useState(20);
  const [vendorModelsSearch, setVendorModelsSearch] = useState('');
  const [vendorAgentsSearch, setVendorAgentsSearch] = useState('');
  const [aiSystemSearch, setAiSystemSearch] = useState('');
  const [vendorAgentsPage, setVendorAgentsPage] = useState(0);
  const [vendorAgentsPageSize, setVendorAgentsPageSize] = useState(20);
  const [organizationUnitId, setOrganizationUnitId] = useState<string | null>(
    null
  );
  const [vendorModelsList, setVendorModelsList] =
    React.useState<ProjectListResponse>({
      data: [],
      totalCount: 0,
    });
  const [vendorAgentsList, setVendorAgentsList] =
    React.useState<ProjectListResponse>({
      data: [],
      totalCount: 0,
    });
  const [availableVendorModelsList, setAvailableVendorModelsList] =
    React.useState<ProjectListResponse>({
      data: [],
      totalCount: 0,
    });

  // Reset projects page when search changes
  React.useEffect(() => {
    setAiSystemPage(0);
  }, [aiSystemSearch]);

  // check if on vendor models page
  const dashboardHelper = dashboardRouteUtil.useHelper();
  const isOnVendorModelsPage = dashboardHelper.matchRoute('VENDOR_MODEL');

  const {
    data: modelsData,
    loading: modelsLoading,
    error: modelsError,
    refetch: refetchModels,
  } = GQL.useModelsListQuery({
    variables: {
      pagination: {
        page: datasetPage,
        pageSize: datasetPageSize,
        riskFilter,
        sortDirection,
      },
      aiSystemId: projectId!,
      functionalModelId: functionalModelId!,
    },
    skip: !projectId || !functionalModelId,
    fetchPolicy: 'network-only',
  });

  const {
    data: agentsData,
    loading: agentsLoading,
    error: agentsError,
    refetch: refetchAgents,
  } = GQL.useAgentListQuery({
    variables: {
      pagination: {
        page: agentsPage,
        pageSize: agentsPageSize,
        riskFilter,
        sortDirection,
      },
      aiSystemId: projectId!,
      functionalModelId: functionalModelId!,
    },
    skip: !projectId || !functionalModelId,
    fetchPolicy: 'network-only',
  });

  // Fetch project data to determine if it's an Organization type
  const { data: projectData } = GQL.useProjectTypeFromIdQuery({
    variables: { id: projectId! },
    skip: !projectId,
    fetchPolicy: 'cache-first',
  });

  const isOrganization =
    projectData?.projectFromId?.projectType === GQL.ProjectType.Organization;

  const {
    data: functionalModelsData,
    loading: functionalModelsLoadingState,
    error: functionalModelsError,
    refetch: refetchFunctionalModels,
  } = GQL.useFunctionalModelsListQuery({
    variables: {
      aiSystemId: projectId!,
      pagination: {
        page: functionalModelsPage,
        pageSize: functionalModelsPageSize,
        riskFilter,
        sortDirection,
      },
    },
    skip: !projectId || !projectData?.projectFromId || isOrganization,
    fetchPolicy: 'network-only',
  });
  const {
    data: datasetData,
    loading: datasetLoadingState,
    error: datasetError,
    refetch: refetchDatasets,
  } = GQL.useDatasetsListQuery({
    variables: {
      aiSystemId: projectId!,
      pagination: {
        page: datasetPage,
        pageSize: datasetPageSize,
        riskFilter,
        sortDirection,
      },
    },
    skip: !projectId || !projectData?.projectFromId || isOrganization,
    fetchPolicy: 'network-only',
  });
  // Fetch AI system list when on Compliance, Projects, or Organization dashboard
  const needsAiSystemList =
    dashboardHelper.matchRoute('COMPLIANCE') ||
    dashboardHelper.matchRoute('PROJECTS') ||
    dashboardHelper.matchRoute('ORGANIZATION');
  const {
    data: aiSystemList,
    previousData: previousAiSystemListData,
    loading: aiSystemListLoading,
    error: aiSystemListError,
    refetch: refetchAiSystemList,
  } = GQL.useAiSystemListQuery({
    variables: {
      ...(aiSystemSearch && { search: aiSystemSearch }),
      pagination: {
        page: aiSystemPage,
        pageSize: aiSystemPageSize,
        riskFilter,
        sortDirection,
      },
      organizationUnitId,
    },
    skip: !needsAiSystemList,
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
  });
  const resolvedAiSystemList = aiSystemList ?? previousAiSystemListData;
  const resolvedAiSystemListLoading =
    aiSystemListLoading && !resolvedAiSystemList;

  const {
    data: vendorModelsData,
    loading: vendorModelsLoading,
    error: vendorModelsError,
    refetch: refetchVendorModels,
  } = GQL.useVendorModelList_Query({
    variables: {
      ...(vendorModelsSearch && { search: vendorModelsSearch }),
      pagination: {
        page: vendorModelsPage,
        pageSize: vendorModelsPageSize,
        riskFilter,
        sortDirection,
      },
    } as any,

    // Only fetch when on vendor models page
    fetchPolicy: 'network-only',
  });
  const {
    data: vendorAgentsData,
    loading: vendorAgentsLoading,
    error: vendorAgentsError,
    refetch: refetchVendorAgents,
  } = GQL.useVendorAgentList_Query({
    variables: {
      ...(vendorAgentsSearch && { search: vendorAgentsSearch }),
      pagination: {
        page: vendorAgentsPage,
        pageSize: vendorAgentsPageSize,
        riskFilter,
        sortDirection,
      },
    } as any,
  });

  // Derive actual loading states that account for errors
  const functionalModelsLoading =
    functionalModelsLoadingState && !functionalModelsError;
  const datasetLoading = datasetLoadingState && !datasetError;
  const loading = modelsLoading && !modelsError;
  const agentsLoading_ = agentsLoading && !agentsError;

  // Error handling
  React.useEffect(() => {
    const error =
      modelsError ?? functionalModelsError ?? datasetError ?? vendorModelsError;
    if (error) {
      Snackbar.open(error);
    }
  }, [modelsError, functionalModelsError, datasetError, vendorModelsError]);

  React.useEffect(() => {
    if (!loading && !modelsError && modelsData) {
      const list = modelsData.modelsList;
      setModelsList({
        data: list.projects.map((project) => ({
          id: project._id,
          projectName: project.name,
          risk: project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: project.completionPercent ?? 0,
          numControls: project.numControls ?? 0,
          numTestScores: project.numTestScores ?? 0,
          policyAlignment:
            project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          projectType: project.projectType,
          source: project.source ?? 'Azure',
          sharedWith: (project.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [modelsData]);

  React.useEffect(() => {
    if (!agentsLoading_ && !agentsError && agentsData) {
      const list = agentsData.agentList;
      setAgentsList({
        data: list.projects.map((project) => ({
          id: project._id,
          projectName: project.name,
          risk: project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: project.completionPercent ?? 0,
          numControls: project.numControls ?? 0,
          numTestScores: project.numTestScores ?? 0,
          policyAlignment:
            project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          projectType: project.projectType,
          source: project.source ?? 'Azure',
          sharedWith: (project.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [agentsData]);

  React.useEffect(() => {
    if (
      !functionalModelsLoading &&
      !functionalModelsError &&
      functionalModelsData
    ) {
      const list = functionalModelsData.functionalModelsList;
      setFunctionalModelsList({
        data: list.projects.map((project) => ({
          id: project._id,
          projectName: project.name,
          risk: project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: project.completionPercent ?? 0,
          numControls: project.numControls ?? 0,
          numTestScores: project.numTestScores ?? 0,
          policyAlignment:
            project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          source: project.source ?? 'Azure',
          sharedWith: (project.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [functionalModelsData, functionalModelsLoading]);

  React.useEffect(() => {
    if (!datasetLoading && !datasetError && datasetData) {
      const list = datasetData?.datasetsList ?? [];

      setDatasetList({
        data: list.projects.map((project) => ({
          id: project._id,
          projectName: project.name,
          risk: project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: project.completionPercent ?? 0,
          numControls: project.numControls ?? 0,
          numTestScores: project.numTestScores ?? 0,
          policyAlignment:
            project.riskStatus ?? GQL.AssessmentRiskStatusType.High,
          source: project.source ?? 'Azure',
          sharedWith: (project.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [datasetData, datasetLoading]);

  React.useEffect(() => {
    if (!vendorModelsLoading && !vendorModelsError && vendorModelsData) {
      const list = vendorModelsData.vendorModelList_;
      if (!list) {
        return;
      }

      setVendorModelsList({
        data: list.projects.map((model) => ({
          id: model._id,
          projectName: model.name,
          risk: GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: model.completionPercent ?? 0,
          numControls: 0,
          numTestScores: 0,
          policyAlignment: GQL.AssessmentRiskStatusType.High,
          createdBy: model.createdBy,
          createdAt: model.createdAt,
          source: model.source ?? 'Azure',
          modelDescription: model.modelDescription ?? undefined,
          modelOwner: model.modelOwner ?? undefined,
          modelArchitecture: model.modelArchitecture ?? undefined,
          sharedWith: (model.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [vendorModelsData, vendorModelsLoading, vendorModelsError]);

  React.useEffect(() => {
    if (!vendorAgentsLoading && !vendorAgentsError && vendorAgentsData) {
      const list = vendorAgentsData.vendorAgentList_;
      if (!list) {
        return;
      }

      setVendorAgentsList({
        data: list.projects.map((agent) => ({
          id: agent._id,
          projectName: agent.name,
          risk: GQL.AssessmentRiskStatusType.High,
          assessmentCompletion: agent.completionPercent ?? 0,
          numControls: 0,
          numTestScores: 0,
          policyAlignment: GQL.AssessmentRiskStatusType.High,
          createdBy: agent.createdBy,
          createdAt: agent.createdAt,
          source: agent.source ?? 'Azure',
          modelDescription: agent.modelDescription ?? undefined,
          modelOwner: agent.modelOwner ?? undefined,
          modelArchitecture: agent.modelArchitecture ?? undefined,
          sharedWith: (agent.sharedWith ?? []).filter(
            (m): m is GQL.SharedMembers => m !== null
          ),
        })),
        totalCount: list.totalCount,
      });
    }
  }, [vendorAgentsData, vendorAgentsLoading, vendorAgentsError]);

  const contextValue = React.useMemo(() => {
    return {
      modelsList,
      datasetLoading,
      functionalModelsLoading,
      agentsLoading: loading, // For now, use same loading state
      datasetList,
      agentsList,
      functionalModelsList,
      loading,
      aiSystemListLoading: resolvedAiSystemListLoading,
      aiSystemList: resolvedAiSystemList,
      aiSystemListError,
      refetchAiSystemList,
      aiSystemSearch,
      setAiSystemSearch,
      refetchDatasets,
      refetchFunctionalModels,
      refetchModels,
      error:
        modelsError ??
        functionalModelsError ??
        datasetError ??
        vendorModelsError,
      functionalModelsPage,
      setFunctionalModelsPage,
      functionalModelsPageSize,
      setFunctionalModelsPageSize,
      setAiSystemPage,
      aiSystemPage,
      aiSystemPageSize,
      setAiSystemPageSize,
      datasetPage,
      agentsData,
      refetchAgents,
      datasetPageSize,
      setDatasetPageSize,
      setDatasetPage,
      agentsPage,
      setAgentsPage,
      agentsPageSize,
      setAgentsPageSize,
      setSortDirection,
      sortDirection,
      functionalModelsTotalCount:
        functionalModelsData?.functionalModelsList.totalCount ?? 0,
      riskFilter,
      setRiskFilter,
      vendorModelsList,
      vendorModelsLoading,
      vendorModelsError,
      refetchVendorModels,
      vendorModelsPage,
      setVendorModelsPage,
      vendorModelsPageSize,
      setVendorModelsPageSize,
      vendorModelsSearch,
      setVendorModelsSearch,
      vendorAgentsList,
      vendorAgentsLoading,
      vendorAgentsError,
      refetchVendorAgents,
      vendorAgentsPage,
      setVendorAgentsPage,
      vendorAgentsPageSize,
      setVendorAgentsPageSize,
      vendorAgentsSearch,
      setVendorAgentsSearch,
      organizationUnitId,
      setOrganizationUnitId,
    };
  }, [
    modelsList,
    agentsList,
    loading,
    functionalModelsLoading,
    datasetLoading,
    functionalModelsList,
    datasetList,
    refetchDatasets,
    refetchFunctionalModels,
    refetchModels,
    refetchAgents,
    refetchVendorAgents,
    modelsError,
    functionalModelsError,
    datasetError,
    vendorModelsError,
    functionalModelsPage,
    functionalModelsPageSize,
    functionalModelsData,
    resolvedAiSystemListLoading,
    resolvedAiSystemList,
    aiSystemListError,
    refetchAiSystemList,
    aiSystemSearch,
    setAiSystemSearch,
    setSortDirection,
    riskFilter,
    setRiskFilter,
    vendorModelsList,
    vendorModelsLoading,
    vendorModelsError,
    refetchVendorModels,
    vendorModelsPage,
    vendorModelsPageSize,
    vendorModelsSearch,
    availableVendorModelsList,
    vendorAgentsList,
    vendorAgentsLoading,
    vendorAgentsError,
    refetchVendorAgents,
    vendorAgentsPage,
    vendorAgentsPageSize,
    vendorAgentsSearch,
    setVendorAgentsSearch,
    organizationUnitId,
    setOrganizationUnitId,
  ]);
  return (
    <ModelsDashboardContext.Provider value={contextValue}>
      {children}
    </ModelsDashboardContext.Provider>
  );
};
